import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, lighten, Typography, useTheme } from '@mui/material';
import {
    Accordion,
    CardSection,
    CenterCircularProgress,
    LinkButton,
    SelectSocieteCard,
    SimplePanelSkeleton,
    SkeletonLoader,
    useModal,
} from '@europrocurement/flexy-components';
import {
    DossierOpenSearch,
    selectedPrescripteursSelector,
    SousClient,
    SousClientApi,
    SousClientsSelector,
    useFormaliteService,
    useTiersService,
    useTiersServiceSelector,
} from '@europrocurement/l2d-domain';
import { faBuildingUser, faUser } from '@fortawesome/pro-regular-svg-icons';
import { SelectSociete } from '@europrocurement/l2d-modules/modules/ParcoursFormalite';
import { DisplayedDocument } from '@europrocurement/l2d-modules/modules/Dossiers/providers/types';
import {
    DossierRoutingContext,
    FormalitiesAndDocumentsSelectorsForm,
} from '@europrocurement/l2d-modules/modules/Dossiers';
import {
    CollectionDeDossierApiApiCollectionsDossiersIdDuplicatePutRequest,
    FormaliteJsonldFormaliteRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';
import { castIriToId } from '@europrocurement/l2d-utils';
import SelectPrescriber from '@europrocurement/l2d-modules/modules/PrescriberClient/components/SelectPrescriber/SelectPrescriber';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@europrocurement/flexy-components/redux/storeConfig/store';
import {
    type CustomCollectionOpenSearch,
    useDossierDocumentsCtx,
} from '@europrocurement/l2d-modules/providers';
import OrderExpandAccordionLabel from './OrderExpandAccordionLabel';
import OrderTitleAccordion from './OrderTitleAccordion';

export type FormalitiesAndDocs = FormaliteJsonldFormaliteRead & {
    displayedDocs?: DisplayedDocument[];
    prestationId?: number;
    reinitForm?: boolean;
};

export type DossFormalitiesAndDocsType = {
    dossierId: number;
    dossierNumero: string;
    dossierSiren: string;
    societeRef: string;
    formalities: FormalitiesAndDocs[];
};

type OrderDuplicateFormProps = {
    dossiers: DossierOpenSearch[];
    collection: CustomCollectionOpenSearch;
};
const OrderDuplicateForm: React.FunctionComponent<OrderDuplicateFormProps> = function ({
    dossiers,
    collection,
}) {
    const datasource = useTiersServiceSelector(SousClientsSelector).main;
    const prescripteur = useTiersServiceSelector(selectedPrescripteursSelector);
    const { modalActions } = useModal();
    const { palette } = useTheme();
    const { formaliteApi, collectionApi } = useFormaliteService();
    const { selectPrescripteur, sousClientsApi } = useTiersService();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch<AppDispatch>();
    const { getOrderRoute, getDossierValidationRoute } = useContext(DossierRoutingContext);
    const { dosDocsloaded, getDossierDocuments } = useDossierDocumentsCtx();

    const didInitRef = useRef(false);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [canSubmit, setCanSubmit] = useState<boolean>(false);
    const [processing, setProcessing] = useState<boolean>(false);
    const [dossiersFormalitiesAndDocs, setDossiersFormalitiesAndDocs] = useState<
        DossFormalitiesAndDocsType[]
    >([]);
    const [selectedDocuments, setSelectedDocuments] = useState<DisplayedDocument[]>([]);
    const [selectedFormalities, setSelectedFormalities] = useState<FormalitiesAndDocs[]>([]);
    const [operatedClient, setOperatedClient] = useState<SousClientApi>();
    const [billedClient, setBilledClient] = useState<SousClientApi>();
    const [sousClients, setSousClients] = useState<SousClient[]>();

    // get tiers/client related to the collection
    useEffect(() => {
        if (datasource.status === 'succeeded') {
            setSousClients(datasource.data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datasource.status]);

    const ready = dossiers.length > 0 && sousClients && dosDocsloaded;

    // Init states
    // Set current operated and billed society, and select all documents by default
    useEffect(() => {
        if (!ready) return;

        if (didInitRef.current) return;
        didInitRef.current = true;

        setDossiersFormalitiesAndDocs([]);
        setSelectedFormalities([]);

        const docs = getDossierDocuments();

        // preselect all documents
        setSelectedDocuments(docs);

        // set default operated society
        if (!operatedClient) {
            const foundOperated = sousClients.find((os) => os.id === dossiers[0].clientOpereId);
            if (foundOperated) {
                setOperatedClient(foundOperated);
            } else if (dossiers[0].clientOpereId) {
                // Fallback on api request if the sousClient is not found
                sousClientsApi
                    .getTiersTiersItem({
                        id: dossiers[0].clientOpereId.toString(),
                        xIdSociete: collection.societeId as number,
                    })
                    .then((res) => {
                        setOperatedClient(res.data as SousClientApi);
                    });
            }
        }
        // set default billed society
        if (!billedClient) {
            const foundBilled = sousClients.find((bs) => bs.id === dossiers[0].clientId);
            if (foundBilled) {
                setBilledClient(foundBilled);
            } else if (dossiers[0].clientId) {
                // Fallback on api request if the sousClient is not found
                sousClientsApi
                    .getTiersTiersItem({
                        id: dossiers[0].clientId.toString(),
                        xIdSociete: collection.societeId as number,
                    })
                    .then((res) => setBilledClient(res.data as SousClientApi));
            }
        }

        const dossiersFormaAndDocs: DossFormalitiesAndDocsType[] = [];

        dossiers.map((dossier) => {
            // The dossier -> formalities -> documents to hydrate
            const dossFormaAndDocs: DossFormalitiesAndDocsType = {
                dossierId: dossier.id as number,
                dossierNumero: dossier.numero as string,
                dossierSiren: dossier.siren as string,
                societeRef: dossier.societeRef as string,
                formalities: [],
            };

            // Loop thru DossierOpenSearch Formalities to extract detailed formalities and documents datas from apis
            dossier.formalites.map(async (formalite) => {
                // Update formalities property with complete formalities datas
                const formaliteRequest = await formaliteApi.apiFormalitesIdGet({
                    id: formalite.id.toString(),
                });

                if (formaliteRequest.status === 200) {
                    const formaliteResponse = formaliteRequest.data as FormalitiesAndDocs;

                    // Update displayedDocuments property with complete documents datas
                    const displayedDocs = formaliteResponse.documents
                        .map((docTypeId) =>
                            docs?.find(
                                (document) => document.documentTypeId === castIriToId(docTypeId),
                            ),
                        )
                        .filter((doc) => doc !== undefined);

                    const formaliteStr: FormalitiesAndDocs = {
                        ...formaliteResponse,
                        displayedDocs,
                        reinitForm: false,
                        prestationId:
                            formalite.prestationId.length === 1
                                ? formalite.prestationId[0]
                                : undefined,
                    };

                    setSelectedFormalities((prevState) => [...prevState, formaliteStr]);

                    // hydrate dossFormalitiesAndDoc Object and psuh it to the state array
                    dossFormaAndDocs.formalities.push(formaliteStr);
                } else {
                    enqueueSnackbar(
                        'Une erreur est survenue lors de la récupération des documents liés à la prestation',
                        { variant: 'error' },
                    );
                }
            });
            // Push the dossier to the array of DossFormalitiesAndDocsType
            dossiersFormaAndDocs.push(dossFormaAndDocs);

            return true;
        });
        setDossiersFormalitiesAndDocs(dossiersFormaAndDocs);

        setIsLoading(false);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossiers, sousClients, ready, operatedClient, billedClient]);

    useEffect(() => {
        if (operatedClient && billedClient && selectedFormalities.length > 0 && prescripteur) {
            setCanSubmit(true);
        } else {
            setCanSubmit(false);
        }
    }, [operatedClient, billedClient, selectedFormalities, prescripteur]);

    const handleSubmit = async () => {
        // Controls
        if (!prescripteur) {
            enqueueSnackbar("Le prescripteur n'est pas défini", { variant: 'error' });
            return;
        }
        if (!billedClient) {
            enqueueSnackbar("La société facturée n'est pas définie", { variant: 'error' });
            return;
        }
        if (!operatedClient) {
            enqueueSnackbar("La société opérée n'est pas définie", { variant: 'error' });
            return;
        }
        if (selectedFormalities.length === 0) {
            enqueueSnackbar("Aucune formalité n'est sélectionnée", { variant: 'error' });
            return;
        }

        if (collection.id) {
            setProcessing(true);
            const request: CollectionDeDossierApiApiCollectionsDossiersIdDuplicatePutRequest = {
                id: collection.id,
                apiCollectionsDossiersIdDuplicatePutRequest: {
                    billedClientId: billedClient.id,
                    documentsIds: selectedDocuments.map((doc) => doc.id as number),
                    operatedClientId: operatedClient.id,
                    prescriberId: prescripteur.id,
                    prestationsIds: selectedFormalities.map(
                        (forma) => forma.prestationId as number,
                    ),
                    reinitFormPrestationsIds: selectedFormalities
                        .filter((sf) => sf.reinitForm === true)
                        .map((forma) => forma.prestationId as number),
                },
            };
            try {
                const response = await collectionApi.apiCollectionsDossiersIdDuplicatePut(request);
                if (response.status === 200) {
                    let dossierValidationRoute = null;
                    let newCollectionRoute = null;
                    if (
                        response.data.dossiers &&
                        response.data.dossiers.length > 0 &&
                        response.data.dossiers[0].id
                    ) {
                        dossierValidationRoute = getDossierValidationRoute(
                            response.data.dossiers[0].id,
                        );
                    }

                    if (response.data.id) {
                        newCollectionRoute = getOrderRoute(response.data.id);
                    }
                    modalActions.call(
                        <Box>
                            <Typography variant="l2dh6">
                                La commande a bien été dupliquée
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '16px',
                                    paddingTop: '24px',
                                }}
                            >
                                <LinkButton
                                    to={getOrderRoute(collection.id)}
                                    onClick={() => modalActions.close()}
                                    label="Retour à la commande"
                                    wrapperSx={{
                                        backgroundColor: palette.background.paper,
                                        '&:hover': {
                                            backgroundColor: palette.background.paper,
                                        },
                                    }}
                                    textSx={{ color: palette.primary.dark }}
                                />
                                {newCollectionRoute && (
                                    <LinkButton
                                        to={newCollectionRoute}
                                        onClick={() => modalActions.close()}
                                        label="Voir la commande générée"
                                    />
                                )}
                                {dossierValidationRoute && (
                                    <LinkButton
                                        to={dossierValidationRoute}
                                        onClick={() => modalActions.close()}
                                        label="Vérifier les informations générées"
                                    />
                                )}
                            </Box>
                        </Box>,
                    );
                } else {
                    enqueueSnackbar('Une erreur est survenue lors de duplication', {
                        variant: 'error',
                    });
                }
            } catch (err) {
                enqueueSnackbar('Une erreur est survenue lors de duplication', {
                    variant: 'error',
                });
                console.log(err);
            }
            setProcessing(false);
        }
        setProcessing(false);
    };

    const handleUpdatePrescriber = () => {
        modalActions.call(
            <SelectPrescriber
                multiple={false}
                onSelect={(ids, prescriberSelected) => {
                    if (prescriberSelected.length === 1) {
                        if (
                            prescriberSelected[0].id &&
                            prescripteur &&
                            prescriberSelected[0].id !== prescripteur.id
                        ) {
                            // Dispatch the newPrescriber
                            dispatch(selectPrescripteur({ id: prescriberSelected[0].id }));

                            // Reset Societies
                            setBilledClient(undefined);
                            setOperatedClient(undefined);
                        }
                    }
                    modalActions.close();
                }}
            />,
        );
    };

    const handleUpdateSociete = (
        type: 'OPERATED' | 'BILLED',
        onSelect: React.Dispatch<React.SetStateAction<SousClientApi | undefined>>,
    ) => {
        if (prescripteur) {
            let title = 'Pour quelle(s) société(s) est destinée la commande ?';
            if (type === 'OPERATED') {
                title = 'Pour quelle société opérée est destinée la commande ?';
            } else if (type === 'BILLED') {
                title = 'Pour quelle société facturée est destinée la commande ?';
            }

            const selectSociety = (society: SousClientApi | undefined) => {
                onSelect(society);
                // On change on the operated society,  update the billed client directly if undefined yet
                if (type === 'OPERATED' && !billedClient) {
                    setBilledClient(society);
                }
            };

            modalActions.call(
                <SelectSociete
                    title={title}
                    description=""
                    multiple={false}
                    idFormalites={[]}
                    onSelect={(ids, sousClientSelect) => {
                        if (sousClientSelect.length === 1) {
                            selectSociety(sousClientSelect[0]);
                        } else {
                            selectSociety(
                                sousClients!.find(
                                    (client) => client.id === dossiers[0].clientOpereId,
                                ),
                            );
                        }
                        modalActions.close();
                    }}
                />,
            );
        }
    };

    return (
        <SkeletonLoader
            isLoading={isLoading}
            skeleton={
                <SimplePanelSkeleton
                    width="100%"
                    height="780px"
                    sx={{ marginTop: '-60px' }}
                />
            }
        >
            <CardSection
                header={
                    <Box>
                        <Typography variant="l2dh6">Dupliquer la commande</Typography>
                    </Box>
                }
                sxHeader={{ padding: '24px 32px 0 32px' }}
                sx={{ display: 'grid', gap: '16px' }}
                sxContent={{ padding: '0 30px 30px 30px', display: 'grid', gap: '16px' }}
            >
                {/* Prescriber info and edit action */}
                <SelectSocieteCard
                    title="Prescripteur"
                    company={
                        prescripteur
                            ? {
                                  name: prescripteur.raisonSociale,
                                  identifier: prescripteur?.siren as string,
                              }
                            : undefined
                    }
                    icon={faUser}
                    onClick={() => handleUpdatePrescriber()}
                />

                {/* Operated society info and edit action */}
                <SelectSocieteCard
                    title="Société opérée"
                    company={
                        operatedClient
                            ? {
                                  name: operatedClient.raisonSociale,
                                  identifier: operatedClient?.siren as string,
                              }
                            : undefined
                    }
                    icon={faBuildingUser}
                    onClick={() => handleUpdateSociete('OPERATED', setOperatedClient)}
                />

                {/* billedClient society info and edit action */}
                <SelectSocieteCard
                    title="Société facturée"
                    company={
                        billedClient
                            ? {
                                  name: billedClient.raisonSociale,
                                  identifier: billedClient?.siren as string,
                              }
                            : undefined
                    }
                    icon={faBuildingUser}
                    onClick={() => handleUpdateSociete('BILLED', setBilledClient)}
                />

                <Box>
                    <Typography variant="l2dsubtitles">
                        Sélection des dossiers, prestations et documents
                    </Typography>
                    {dossiersFormalitiesAndDocs && (
                        <Box sx={{ display: 'grid', gap: '8px' }}>
                            {dossiersFormalitiesAndDocs.map((dossierFormalitiesAndDocs) => (
                                <Accordion
                                    title={
                                        <OrderTitleAccordion
                                            dossierFormalitiesAndDocs={dossierFormalitiesAndDocs}
                                        />
                                    }
                                    expandLabel={
                                        <OrderExpandAccordionLabel
                                            dossierFormalitiesAndDocs={dossierFormalitiesAndDocs}
                                            selectedFormalities={selectedFormalities}
                                            selectedDocuments={selectedDocuments}
                                        />
                                    }
                                    expanded={false}
                                    expandColor={palette.primary}
                                    key={dossierFormalitiesAndDocs.dossierId}
                                    sx={{ backgroundColor: palette.background.paper }}
                                >
                                    <FormalitiesAndDocumentsSelectorsForm
                                        dossFormalitiesAndDocs={
                                            dossierFormalitiesAndDocs.formalities
                                        }
                                        selectedFormalities={selectedFormalities}
                                        setSelectedFormalities={setSelectedFormalities}
                                        setSelectedDocuments={setSelectedDocuments}
                                        selectedDocuments={selectedDocuments}
                                    />
                                </Accordion>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        columnGap: '16px',
                    }}
                >
                    <Button
                        variant="contained"
                        sx={{
                            padding: '16px 24px',
                            minWidth: 'auto',
                            lineHeight: '24px',
                            backgroundColor: canSubmit ? palette.primary.dark : palette.grey['300'],
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0 10px',
                            '&:hover': {
                                backgroundColor: lighten(
                                    canSubmit ? palette.primary.dark : palette.grey['300'],
                                    0.2,
                                ),
                            },
                        }}
                        onClick={canSubmit ? handleSubmit : () => {}}
                        disabled={processing}
                    >
                        {processing && (
                            <CircularProgress
                                color="inherit"
                                size="16px"
                            />
                        )}
                        Dupliquer
                    </Button>
                </Box>
            </CardSection>

            {processing && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1,
                    }}
                >
                    <CenterCircularProgress />
                </Box>
            )}
        </SkeletonLoader>
    );
};

export default OrderDuplicateForm;
